import {
  PaymentRequest,
  PaymentResponse,
  MaturityPaymentRequest,
  PaymentStatisticsResponse,
} from '../../model/Payment.model';
import { UnpaidCardResponse } from '../../model/UnpaidCard.model';
import axiosInstance from '../../utils/axiosInstance';

// Interface cho ApiResponse
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Interface cho UpdatePaymentRequest
interface UpdatePaymentRequest {
  amount: number;
  paymentDate: string;
  notes?: string;
}

// Interface cho PageResponse
interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

class PaymentService {
  private readonly basePath = '/payment';

  /**
   * Ghi nhận thanh toán cho kỳ đáo hạn
   */
  async createMaturityPayment(
    maturityScheduleId: number,
    paymentData: MaturityPaymentRequest,
    employeeId: number
  ): Promise<PaymentResponse> {
    try {
      const response = await axiosInstance.post<PaymentResponse>(
        `${this.basePath}/maturity/${maturityScheduleId}?employeeId=${employeeId}`,
        paymentData
      );
      return response.data;
    } catch (error) {
      console.error('Error creating maturity payment:', error);
      throw error;
    }
  }

  /**
   * Lấy các khoản thanh toán kỳ đáo hạn theo tháng và năm
   */
  async getMaturityPaymentsByMonth(
    year: number,
    month: number,
    page: number = 0,
    size: number = 10
  ): Promise<ApiResponse<PageResponse<PaymentResponse>>> {
    try {
      const response = await axiosInstance.get<
        ApiResponse<PageResponse<PaymentResponse>>
      >(
        `${this.basePath}/maturity/by-month?year=${year}&month=${month}&page=${page}&size=${size}`
      );

      console.log('Payment service response:', response.data);

      // Ensure response structure is valid
      const data = response.data;
      if (!data || !data.data) {
        return {
          success: true,
          message: 'No payments found',
          data: {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: size,
            number: page,
            first: true,
            last: true,
          },
        };
      }

      // Ensure content array exists
      if (!data.data.content) {
        data.data.content = [];
      }

      return data;
    } catch (error: any) {
      console.error('Error getting maturity payments by month:', error);

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No payments found',
          data: {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: size,
            number: page,
            first: true,
            last: true,
          },
        };
      }

      throw error;
    }
  }

  /**
   * Lấy các khoản thanh toán kỳ đáo hạn theo tháng, năm và nhân viên (cho ROLE_EMPLOYEE)
   */
  async getMaturityPaymentsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number,
    page: number = 0,
    size: number = 10
  ): Promise<ApiResponse<PageResponse<PaymentResponse>>> {
    try {
      const response = await axiosInstance.get<
        ApiResponse<PageResponse<PaymentResponse>>
      >(
        `${this.basePath}/maturity/by-month-and-employee?year=${year}&month=${month}&employeeId=${employeeId}&page=${page}&size=${size}`
      );

      console.log('Payment service response (by employee):', response.data);

      // Ensure response structure is valid
      const data = response.data;
      if (!data || !data.data) {
        return {
          success: true,
          message: 'No payments found',
          data: {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: size,
            number: page,
            first: true,
            last: true,
          },
        };
      }

      // Ensure content array exists
      if (!data.data.content) {
        data.data.content = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting maturity payments by month and employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No payments found',
          data: {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: size,
            number: page,
            first: true,
            last: true,
          },
        };
      }

      throw error;
    }
  }

  // Lấy tất cả payment của một maturity schedule
  async getPaymentsByMaturitySchedule(
    maturityScheduleId: number
  ): Promise<PaymentResponse[]> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/maturity/${maturityScheduleId}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error('Error getting payments by maturity schedule:', error);

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw error;
    }
  }

  // Lấy tất cả payment đã xóa của một maturity schedule
  async getDeletedPaymentsByMaturitySchedule(
    maturityScheduleId: number
  ): Promise<ApiResponse<PaymentResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/deleted/maturity/${maturityScheduleId}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No deleted payments found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting deleted payments by maturity schedule:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No deleted payments found',
          data: [],
        };
      }

      throw error;
    }
  }

  /**
   * Cập nhật thông tin payment
   */
  async updatePayment(
    paymentId: number,
    updateData: UpdatePaymentRequest,
    employeeId: number
  ): Promise<ApiResponse<PaymentResponse>> {
    try {
      const response = await axiosInstance.put<ApiResponse<PaymentResponse>>(
        `${this.basePath}/${paymentId}?employeeId=${employeeId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error('Error updating payment:', error);
      throw error;
    }
  }

  /**
   * Xóa payment (soft delete)
   */
  async deletePayment(
    paymentId: number,
    employeeId: number
  ): Promise<ApiResponse<string>> {
    try {
      const response = await axiosInstance.delete<ApiResponse<string>>(
        `${this.basePath}/${paymentId}?employeeId=${employeeId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting payment:', error);
      throw error;
    }
  }

  /**
   * Ghi nhận thanh toán cho giao dịch withdrawal
   */
  async createWithdrawalPayment(
    transactionId: number,
    paymentData: MaturityPaymentRequest,
    employeeId: number
  ): Promise<PaymentResponse> {
    try {
      const response = await axiosInstance.post<PaymentResponse>(
        `${this.basePath}/withdrawal/${transactionId}?employeeId=${employeeId}`,
        paymentData
      );
      return response.data;
    } catch (error) {
      console.error('Error creating withdrawal payment:', error);
      throw error;
    }
  }

  /**
   * Lấy tất cả payment của một giao dịch withdrawal
   */
  async getPaymentsByWithdrawalTransaction(
    transactionId: number
  ): Promise<PaymentResponse[]> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal/${transactionId}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error('Error getting payments by withdrawal transaction:', error);

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw error;
    }
  }

  /**
   * Lấy tất cả payment đã xóa của một giao dịch withdrawal
   */
  async getDeletedPaymentsByWithdrawalTransaction(
    transactionId: number
  ): Promise<ApiResponse<PaymentResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/deleted/withdrawal/${transactionId}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No deleted payments found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting deleted payments by withdrawal transaction:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No deleted payments found',
          data: [],
        };
      }

      throw error;
    }
  }

  /**
   * Lấy các khoản thanh toán kỳ đáo hạn theo ngày/tháng/năm
   */
  async getMaturityPaymentsByDate(
    year: number,
    month: number,
    day: number,
    page: number = 0,
    size: number = 10,
    cardHolderName?: string
  ): Promise<ApiResponse<PageResponse<PaymentResponse>>> {
    const url = `/payment/maturity/by-date?year=${year}&month=${month}&day=${day}&page=${page}&size=${size}${cardHolderName ? `&cardHolderName=${encodeURIComponent(cardHolderName)}` : ''}`;
    const response = await axiosInstance.get<
      ApiResponse<PageResponse<PaymentResponse>>
    >(url);
    return response.data;
  }

  async getMaturityPaymentsByDateAndEmployee(
    year: number,
    month: number,
    day: number,
    employeeId: number,
    page: number = 0,
    size: number = 10,
    cardHolderName?: string
  ): Promise<ApiResponse<PageResponse<PaymentResponse>>> {
    const url = `/payment/maturity/by-date-and-employee?year=${year}&month=${month}&day=${day}&employeeId=${employeeId}&page=${page}&size=${size}${cardHolderName ? `&cardHolderName=${encodeURIComponent(cardHolderName)}` : ''}`;
    const response = await axiosInstance.get<
      ApiResponse<PageResponse<PaymentResponse>>
    >(url);
    return response.data;
  }

  /**
   * Lấy TẤT CẢ các khoản thanh toán kỳ đáo hạn theo ngày (không phân trang) - dùng cho thống kê
   */
  async getAllMaturityPaymentsByDate(
    year: number,
    month: number,
    day: number
  ): Promise<ApiResponse<PageResponse<PaymentResponse>>> {
    // Sử dụng size rất lớn để lấy tất cả dữ liệu
    const response = await axiosInstance.get<
      ApiResponse<PageResponse<PaymentResponse>>
    >(
      `/payment/maturity/by-date?year=${year}&month=${month}&day=${day}&page=0&size=10000`
    );
    return response.data;
  }

  /**
   * Lấy TẤT CẢ các khoản thanh toán kỳ đáo hạn theo ngày và nhân viên (không phân trang) - dùng cho thống kê
   * @deprecated Sử dụng getPaymentStatisticsByDate hoặc getPaymentStatisticsByDateAndEmployee thay thế
   */
  async getAllMaturityPaymentsByDateAndEmployee(
    year: number,
    month: number,
    day: number,
    employeeId: number
  ): Promise<ApiResponse<PageResponse<PaymentResponse>>> {
    // Sử dụng size rất lớn để lấy tất cả dữ liệu
    const response = await axiosInstance.get<
      ApiResponse<PageResponse<PaymentResponse>>
    >(
      `/payment/maturity/by-date-and-employee?year=${year}&month=${month}&day=${day}&employeeId=${employeeId}&page=0&size=10000`
    );
    return response.data;
  }

  /**
   * Lấy thống kê thanh toán kỳ đáo hạn theo ngày (cho admin)
   */
  async getPaymentStatisticsByDate(
    year: number,
    month: number,
    day: number
  ): Promise<ApiResponse<PaymentStatisticsResponse>> {
    const response = await axiosInstance.get<
      ApiResponse<PaymentStatisticsResponse>
    >(
      `/payment/maturity/statistics/by-date?year=${year}&month=${month}&day=${day}`
    );
    return response.data;
  }

  /**
   * Lấy thống kê thanh toán kỳ đáo hạn theo ngày và nhân viên (cho employee)
   */
  async getPaymentStatisticsByDateAndEmployee(
    year: number,
    month: number,
    day: number,
    employeeId: number
  ): Promise<ApiResponse<PaymentStatisticsResponse>> {
    const response = await axiosInstance.get<
      ApiResponse<PaymentStatisticsResponse>
    >(
      `/payment/maturity/statistics/by-date-and-employee?year=${year}&month=${month}&day=${day}&employeeId=${employeeId}`
    );
    return response.data;
  }
}

export default new PaymentService();
